import React, { useState, useEffect, useCallback } from "react";
import { Layers, Plus, Trash2, Tag, ListPlus } from "lucide-react";
import { Card, Button, Badge, Spinner, PageHeader, Modal, Input, Select, EmptyState } from "../../lib/ui";
import { useT } from "../../lib/i18n";
import { useToast } from "../../lib/toast";
import { useAuth } from "../../context/AuthContext";
import api from "../../lib/api";
import { rp, PeriodPicker } from "../../components/finance";

const TYPE_TONE = { REVENUE: "success", COGS: "warn", LABOR: "info", OCCUPANCY: "info", MARKETING: "info", OTHER_OPEX: "mute", NON_OPERATING: "danger", CASH_IN: "success", CASH_OUT: "danger" };

// ---------------- Categories tab ----------------
function CategoriesTab({ canManage }) {
  const { t } = useT();
  const toast = useToast();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", type: "OTHER_OPEX", kind: "MANUAL" });

  const load = useCallback(async () => {
    try { const { data } = await api.get("/finance/categories"); setData(data); }
    catch { toast.error(t("Failed to load")); }
  }, [t, toast]);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.name.trim()) return toast.error(t("Name is required"));
    try { await api.post("/finance/categories", form); toast.success(t("Category added")); setOpen(false); setForm({ name: "", type: "OTHER_OPEX", kind: "MANUAL" }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || t("Failed")); }
  };
  const del = async (id) => {
    try { await api.delete(`/finance/categories/${id}`); toast.success(t("Deleted")); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || t("Failed")); }
  };

  if (!data) return <div className="flex h-40 items-center justify-center"><Spinner className="h-7 w-7" /></div>;

  return (
    <div className="space-y-4">
      {canManage && <div className="flex justify-end"><Button onClick={() => setOpen(true)} data-testid="finance-cat-new"><Plus size={16} /> {t("Add Category")}</Button></div>}
      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="finance-cat-table">
            <thead className="border-b border-line text-left text-xs uppercase tracking-wide text-ink-mute">
              <tr><th className="py-2 pr-3">{t("Category")}</th><th className="py-2 pr-3">{t("Type")}</th><th className="py-2 pr-3">{t("Source")}</th><th className="py-2 pr-3" /></tr>
            </thead>
            <tbody className="divide-y divide-line">
              {data.rows.map((c) => (
                <tr key={c.id} data-testid={`finance-cat-${c.id}`} className={c.active ? "" : "opacity-50"}>
                  <td className="py-2.5 pr-3 font-medium text-ink">{c.name}{c.system && <Badge tone="mute" className="ml-2 !px-1.5 !py-0.5 !text-[10px]">{t("system")}</Badge>}</td>
                  <td className="py-2.5 pr-3"><Badge tone={TYPE_TONE[c.type]}>{c.type}</Badge></td>
                  <td className="py-2.5 pr-3"><Badge tone={c.kind === "AUTO" ? "info" : "mute"} className="!text-[10px]">{c.kind}</Badge></td>
                  <td className="py-2.5 pr-3 text-right">{canManage && !c.system && <button onClick={() => del(c.id)} className="text-ink-mute hover:text-danger-text" data-testid={`finance-cat-del-${c.id}`}><Trash2 size={15} /></button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Modal open={open} onClose={() => setOpen(false)} title={t("Add Finance Category")}
        footer={<><Button variant="ghost" onClick={() => setOpen(false)}>{t("Cancel")}</Button><Button onClick={create} data-testid="finance-cat-save">{t("Save")}</Button></>}>
        <Input label={t("Category Name")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mb-3" data-testid="finance-cat-name" />
        <Select label={t("Type")} value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="finance-cat-type">
          {(data.types || []).map((tp) => <option key={tp} value={tp}>{tp}</option>)}
        </Select>
      </Modal>
    </div>
  );
}

// ---------------- Entries tab ----------------
function EntriesTab({ canManage }) {
  const { t } = useT();
  const toast = useToast();
  const [period, setPeriod] = useState(() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; });
  const [entries, setEntries] = useState(null);
  const [cats, setCats] = useState([]);
  const [branches, setBranches] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ category_id: "", amount: "", date: "", branch_id: "", note: "" });

  const load = useCallback(async () => {
    try { const { data } = await api.get("/finance/entries", { params: { period } }); setEntries(data.rows); }
    catch { toast.error(t("Failed to load")); }
  }, [period, t, toast]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/finance/categories").then(({ data }) => setCats(data.rows.filter((c) => c.kind === "MANUAL" && c.active))).catch(() => {});
    api.get("/branches").then(({ data }) => setBranches(Array.isArray(data) ? data : data.rows || [])).catch(() => {});
  }, []);

  const openNew = () => { setForm({ category_id: "", amount: "", date: `${period}-15`, branch_id: "", note: "" }); setOpen(true); };
  const create = async () => {
    if (!form.category_id || !form.amount || !form.date) return toast.error(t("Category, amount and date are required"));
    try { await api.post("/finance/entries", { ...form, amount: Number(form.amount), branch_id: form.branch_id || null }); toast.success(t("Entry added")); setOpen(false); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || t("Failed")); }
  };
  const del = async (id) => { try { await api.delete(`/finance/entries/${id}`); toast.success(t("Deleted")); load(); } catch { toast.error(t("Failed")); } };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <PeriodPicker value={period} onChange={setPeriod} />
        {canManage && <Button onClick={openNew} data-testid="finance-entry-new"><ListPlus size={16} /> {t("Add Expense / Entry")}</Button>}
      </div>
      <Card>
        {!entries ? <div className="flex h-32 items-center justify-center"><Spinner className="h-7 w-7" /></div>
          : entries.length === 0 ? <EmptyState title={t("No entries this period")} subtitle={t("Add manual expenses like rent, payroll, electricity, marketing, tax.")} />
          : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="finance-entry-table">
                <thead className="border-b border-line text-left text-xs uppercase tracking-wide text-ink-mute">
                  <tr><th className="py-2 pr-3">{t("Date")}</th><th className="py-2 pr-3">{t("Category")}</th><th className="py-2 pr-3">{t("Type")}</th><th className="py-2 pr-3">{t("Branch")}</th><th className="py-2 pr-3 text-right">{t("Amount")}</th><th className="py-2 pr-3" /></tr>
                </thead>
                <tbody className="divide-y divide-line">
                  {entries.map((e) => (
                    <tr key={e.id} data-testid={`finance-entry-${e.id}`}>
                      <td className="py-2.5 pr-3 text-xs text-ink-soft">{e.date}</td>
                      <td className="py-2.5 pr-3 font-medium text-ink">{e.category_name}</td>
                      <td className="py-2.5 pr-3"><Badge tone={TYPE_TONE[e.type]} className="!text-[10px]">{e.type}</Badge></td>
                      <td className="py-2.5 pr-3 text-xs text-ink-soft">{e.branch_name || t("All")}</td>
                      <td className="py-2.5 pr-3 text-right font-semibold text-ink">{rp(e.amount)}</td>
                      <td className="py-2.5 pr-3 text-right">{canManage && <button onClick={() => del(e.id)} className="text-ink-mute hover:text-danger-text" data-testid={`finance-entry-del-${e.id}`}><Trash2 size={15} /></button>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </Card>

      <Modal open={open} onClose={() => setOpen(false)} title={t("Add Expense / Entry")}
        footer={<><Button variant="ghost" onClick={() => setOpen(false)}>{t("Cancel")}</Button><Button onClick={create} data-testid="finance-entry-save">{t("Save")}</Button></>}>
        <Select label={t("Category")} value={form.category_id} onChange={(e) => setForm({ ...form, category_id: e.target.value })} className="mb-3" data-testid="finance-entry-category">
          <option value="">{t("Select category")}</option>
          {cats.map((c) => <option key={c.id} value={c.id}>{c.name} ({c.type})</option>)}
        </Select>
        <div className="mb-3 grid grid-cols-2 gap-3">
          <Input label={t("Amount")} type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="finance-entry-amount" />
          <Input label={t("Date")} type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="finance-entry-date" />
        </div>
        <Select label={t("Branch (optional)")} value={form.branch_id} onChange={(e) => setForm({ ...form, branch_id: e.target.value })} className="mb-3">
          <option value="">{t("All branches")}</option>
          {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
        </Select>
        <Input label={t("Note")} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
      </Modal>
    </div>
  );
}

// ---------------- Page ----------------
export default function FinanceCategories() {
  const { t } = useT();
  const { hasPermission } = useAuth();
  const [tab, setTab] = useState("entries");
  const canManageCat = hasPermission("manage_finance_categories");
  const canManageEntry = hasPermission("manage_finance_entries");

  const tabs = [
    { id: "entries", label: t("Expenses & Entries"), icon: ListPlus },
    { id: "categories", label: t("Categories"), icon: Tag },
  ];

  return (
    <div data-testid="finance-categories-page">
      <PageHeader title={t("Finance Categories")} subtitle={t("Manual expenses & category master")} icon={Layers} />
      <div className="mb-5 inline-flex rounded-xl border border-line bg-white p-1">
        {tabs.map((tb) => (
          <button key={tb.id} data-testid={`finance-cat-tab-${tb.id}`} onClick={() => setTab(tb.id)}
            className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition-colors ${tab === tb.id ? "bg-primary text-white shadow-sm" : "text-ink-soft hover:bg-stone-100"}`}>
            <tb.icon size={16} /> {tb.label}
          </button>
        ))}
      </div>
      {tab === "entries" ? <EntriesTab canManage={canManageEntry} /> : <CategoriesTab canManage={canManageCat} />}
    </div>
  );
}
